import { Component, effect, inject, OnInit, Renderer2, signal } from '@angular/core';
import { AdminAccessService, TranslationService } from '@app/services';
import { Router, RouterEvent, NavigationStart, NavigationEnd, NavigationCancel, NavigationError, ActivatedRoute } from '@angular/router';
import { ApplicationInsightsService } from './services/application-insights-service.service';
import { CookieService } from 'ngx-cookie-service';
import { Parameters } from './components/parameters/parameters';
import { ParametersService } from './components/parameters/parameters.service';
import { User } from '@app/components/user/user';
import { filter, first, Observable, switchMap, take } from 'rxjs';
import { CountryService } from '@app/services/country.service';
import { PoeModalService } from './services/poe-modal.service';
import { UserManagerService } from './managers';
import { AppLink } from './types/app-link.type';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'environments/environment';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '@auth0/auth0-angular';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit {
    appInfos: AppLink[] = [
        {
            name: 'METERING WHEEL ASSIST',
            appStoreUrl: 'https://apps.apple.com/at/app/p%C3%B6tsem/id1434048370?itsct=apps_box_link&itscg=30200',
            icon: 'assets/menu-icons/app-metering-wheel-assist.svg'
        },
        {
            name: 'HARVEST ASSIST',
            playStoreUrl: 'https://play.google.com/store/apps/details?id=at.poettinger.harvestassist',
            appStoreUrl: 'https://apps.apple.com/at/app/harvest-assist/id1573717941?itsct=apps_box_link&itscg=30200',
            icon: 'assets/menu-icons/app-harvest-assist.svg'
        }
    ];


    readonly viewMode = signal<'kiosk' | ''>('');
    readonly configuratorCountries = environment.configuratorRestrictions.map(limitation => limitation.locale);

    isLoading: boolean = true;
    params: Parameters;
    googleAnalytics = false;

    isUserMenuOpened: boolean = false;
    isMobileMenuOpened: boolean = false;

    user$: Observable<User>;
    isAuthenticated = toSignal(this.authService.isAuthenticated$, { initialValue: false });
    canAccessAdmin = this.adminAccessService.canAccessAdmin;
    userMachines = this.usermanagerservice.userMachinesResource;
    private readonly _applicationInsightsService = inject(ApplicationInsightsService);

    constructor(
        private readonly translateService: TranslateService,
        private readonly translationService: TranslationService,
        private readonly cookieService: CookieService,
        private readonly parametersService: ParametersService,
        private readonly usermanagerservice: UserManagerService,
        private readonly renderer: Renderer2,
        private readonly countryService: CountryService,
        private readonly modalService: PoeModalService,
        private readonly activatedRoute: ActivatedRoute,
        private readonly authService: AuthService,
        private readonly adminAccessService: AdminAccessService,
        protected readonly router: Router
    ) {
        let lastAppliedLanguage: string | undefined;
        this.parametersService.getParametersObservable().subscribe(par => {
            this.params = par;
            if (par?.language && par.language !== lastAppliedLanguage && par.language !== this.translateService.currentLang) {
                lastAppliedLanguage = par.language;
                this.translationService.changeLanguage(par.language);
            }
        });
        this.activatedRoute.queryParamMap.subscribe(qp => {
            const viewMode = qp.get('viewMode');
            this.viewMode.set(viewMode === 'kiosk' ? 'kiosk' : '');
        });

        effect(() => {
            if (this.isAuthenticated()) {

                this.usermanagerservice.savePendingMachineConfigurations();
                this.usermanagerservice.getUser();

                this.user$ = this.parametersService.user$;
                this.parametersService.getParametersObservable().pipe(
                    filter(params => !!params.user),
                    first()
                ).subscribe(({ user }) => { // { user } is shorthand for params.user
                    if (user.showWelcome) {
                        this.modalService.showWelcomeModal();
                    }
                });
            }
        });

        effect(() => {
            if (this.isAuthenticated()) {
                return;
            }
            const cookieLang = this.cookieService.get('language');
            if (cookieLang) {
                this.parametersService.setLanguage(cookieLang);
            }
        });
        const localeCode = this.cookieService.get('locale');
        if (localeCode?.length >= 5) {
            this.countryService.getLocaleByCode(localeCode)
                .subscribe(locale => this.parametersService.setCountry(locale));

        } else {

            const parts = navigator.language.split('-');
            if (parts.length === 2) {
                const language = parts[0];
                const region = parts.at(-1)?.toLocaleUpperCase();
                this.countryService.getLocaleByCode(`${language}_${region}`).subscribe(locale => {
                    this.parametersService.setCountry(locale);
                });
            }

        }
        if (this.params.country) {
            this.countryService.getLocaleByCode(this.params.country.code).subscribe(locale => {
                this.params.country.countryName = locale.countryName;
                this.params.country.languageName = locale.languageName;
            });
        }

        this.router.events.subscribe((event: NavigationStart | NavigationEnd) => {
            this.navigationInterceptor(event);
        });

        this.translationService.setupRenderer2(this.renderer);
        this.translationService.setupLanguageSettings();
    }

    public toggleUserMenu() {
        this.isUserMenuOpened = !this.isUserMenuOpened;
    }

    public toggleMobileMenu() {
        this.isMobileMenuOpened = !this.isMobileMenuOpened;
    }

    ngOnInit() {
        this.user$ = this.parametersService.user$;

        if (!globalThis.location.search.includes('code=')) {
            sessionStorage.removeItem('returnUrlAfterLogin');
        }

        this.authService.isLoading$.pipe(
            filter(isLoading => !isLoading),
            take(1),
            switchMap(() => this.authService.isAuthenticated$.pipe(take(1)))
        ).subscribe(isAuthenticated => {
            if (!isAuthenticated) {
                return;
            }
            const target = this.parametersService.consumeStoredLoginRedirectTarget();
            if (!target) {
                return;
            }
            const currentPath = this.router.url.split('?')[0];
            const targetPath = target.split('?')[0];
            if (!this.parametersService.pathsEqual(currentPath, targetPath)) {
                this.router.navigateByUrl(target);
            }
        });

        if (this.cookieService.get('google-analytics') === 'yes') {
            this.googleAnalytics = true;
        }
    }

    closeUserMenu(): void {
        this.isUserMenuOpened = false;
    }

    closeMobileMenu(): void {
        this.isMobileMenuOpened = false;
    }

    logout(): void {
        this.authService.logout({
            logoutParams: {
                returnTo: environment.logoutUrl
            }
        });
    }

    // Shows and hides the loading spinner during RouterEvent changes
    navigationInterceptor(event: RouterEvent): void {
        setTimeout(() => {
            if ((event instanceof NavigationStart) || (event instanceof NavigationEnd)) {
                this.isLoading = true;
            }

            // Set loading state to false in both of the below events to hide the spinner in case a request fails
            if (event instanceof NavigationCancel) {
                this.isLoading = false;
            }
            if (event instanceof NavigationError) {
                this.isLoading = false;
            }
        });
    }

    onClickGA() {
        if (this.googleAnalytics) {
            this.cookieService.set('google-analytics', 'no');
            globalThis.location.reload();
        } else {
            this.cookieService.set('google-analytics', 'yes');
            globalThis.location.reload(); // reload so that the js function in index.html can see the changed GA cookie
        }
    }

    onClickPoetsem(): void {
        globalThis.open('https://www.poettinger.at/poetsem', '_blank', 'noopener,resizable,scrollbars');
        this.isMobileMenuOpened = false;
    }

    openAppPopup(appInfo: AppLink) {
        this.modalService.showAppStoreModal(
            {
                title: appInfo.name
            },
            appInfo);
    }
}
